import type {
  DOMConversionMap,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  LexicalUpdateJSON,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import type { JSX } from 'react';

import { ImageComponent } from './ImageComponent';

export type SerializedImageNode = Spread<
  {
    src: string;
    altText: string;
    /** Set by the host's upload callback so the src can be re-resolved on load, as Froala does. */
    fileId?: string;
    width?: number;
  },
  SerializedLexicalNode
>;

export type ImagePayload = {
  src: string;
  altText?: string;
  fileId?: string;
  width?: number;
  key?: NodeKey;
};

/**
 * Images are a decorator node so the `<img>` stays uneditable inline content. Serialisation
 * round-trips through plain `<img>` markup (with Froala's `data-file-id`), which is what the
 * REST layer already stores for Froala-authored content.
 */
export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string;
  __fileId?: string;
  __width?: number;

  static override getType(): string {
    return 'image';
  }

  static override clone(node: ImageNode): ImageNode {
    return new ImageNode({ src: node.__src, altText: node.__altText, fileId: node.__fileId, width: node.__width }, node.__key);
  }

  // Lexical instantiates a bare node when it registers the class (to read its static config),
  // so the payload has to be optional.
  constructor(payload: Omit<ImagePayload, 'key'> = { src: '' }, key?: NodeKey) {
    super(key);
    this.__src = payload.src;
    this.__altText = payload.altText ?? '';
    this.__fileId = payload.fileId;
    this.__width = payload.width;
  }

  static override importJSON(serialized: SerializedImageNode): ImageNode {
    return $createImageNode({ src: serialized.src, altText: serialized.altText }).updateFromJSON(serialized);
  }

  override updateFromJSON(serialized: LexicalUpdateJSON<SerializedImageNode>): this {
    const self = super.updateFromJSON(serialized);
    const writable = self.getWritable();
    writable.__src = serialized.src;
    writable.__altText = serialized.altText;
    writable.__fileId = serialized.fileId;
    writable.__width = serialized.width;
    return self;
  }

  override exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      src: this.__src,
      altText: this.__altText,
      fileId: this.__fileId,
      width: this.__width,
    };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        priority: 0,
        conversion: (element: HTMLElement) => {
          const img = element as HTMLImageElement;
          const width = Number.parseInt(img.getAttribute('width') ?? '', 10);
          return {
            node: $createImageNode({
              src: img.getAttribute('src') ?? '',
              altText: img.getAttribute('alt') ?? '',
              fileId: img.getAttribute('data-file-id') ?? undefined,
              width: Number.isNaN(width) ? undefined : width,
            }),
          };
        },
      }),
    };
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('src', this.__src);
    element.setAttribute('alt', this.__altText);
    if (this.__fileId) element.setAttribute('data-file-id', this.__fileId);
    if (this.__width) element.setAttribute('width', String(this.__width));
    return { element };
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const className = config.theme.image;
    if (typeof className === 'string') span.className = className;
    return span;
  }

  override updateDOM(): false {
    return false;
  }

  // Accessors used by the image toolbar and resolver; setters write a new node version via `getWritable`
  getSrc(): string {
    return this.__src;
  }

  setSrc(src: string): this {
    const writable = this.getWritable();
    writable.__src = src;
    return writable;
  }

  getFileId(): string | undefined {
    return this.__fileId;
  }

  getAltText(): string {
    return this.__altText;
  }

  setAltText(altText: string): this {
    const writable = this.getWritable();
    writable.__altText = altText;
    return writable;
  }

  getWidth(): number | undefined {
    return this.__width;
  }

  /** `undefined` drops the `width` attribute, rendering the image at its natural size. */
  setWidth(width: number | undefined): this {
    const writable = this.getWritable();
    writable.__width = width;
    return writable;
  }

  override decorate(): JSX.Element {
    return <ImageComponent nodeKey={this.getKey()} src={this.__src} altText={this.__altText} width={this.__width} />;
  }
}

export const $createImageNode = (payload: ImagePayload): ImageNode => $applyNodeReplacement(new ImageNode(payload, payload.key));

export const $isImageNode = (node: LexicalNode | null | undefined): node is ImageNode => node instanceof ImageNode;
